import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import API from "../../../lib/api";
import { Button } from "../../ui/button";
import { Pill, fmtDT } from "../hsUtils";
import { TONE_CLASSES } from "../../assets/assetUtils";
import { HandHeart, X } from "@phosphor-icons/react";
import { KindPill, err } from "./libraryUtils";

const STATUS_TONE = { pending: TONE_CLASSES.soon, accepted: TONE_CLASSES.ok, declined: TONE_CLASSES.overdue, withdrawn: TONE_CLASSES.none };
const STATUS_TEXT = { pending: "Waiting for the platform team", accepted: "Accepted — being prepared", declined: "Not taken forward", withdrawn: "Withdrawn" };

/** Tenant: what this workspace has proposed to the library and where each proposal got to. */
export function MyProposals({ canEdit, highlight = false }) {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => API.get("/hs/library/proposals").then((r) => setRows(r.data.proposals)).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);
  if (!rows?.length) return null;
  const withdraw = async (p) => {
    try { await API.delete(`/hs/library/proposals/${p.id}`); toast.success("Withdrawn"); load(); } catch (e) { err(e, "Couldn't withdraw"); }
  };
  return (
    <section className={`rounded-lg border border-border bg-card p-4 ${highlight ? "ring-2 ring-primary/20 border-primary" : ""}`} data-testid="hs-library-proposals">
      <h3 className="text-sm font-semibold flex items-center gap-2"><HandHeart size={16} className="text-primary" /> Your proposals to the library</h3>
      <p className="text-xs text-muted-foreground mt-0.5">Accepted items are published anonymously once the platform team has removed site and client details.</p>
      <ul className="mt-3 divide-y divide-border/60">
        {rows.map((p) => (
          <li key={p.id} className="py-2 flex flex-wrap items-center gap-2" data-testid={`hs-library-proposal-${p.id}`}>
            <KindPill kind={p.kind} />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">{p.title} <span className="text-muted-foreground font-normal">· your {p.source?.label}</span></p>
              <p className="text-xs text-muted-foreground truncate">{p.removed_at ? "Was in the library — since removed by the platform team" : p.published_at ? "Live in the library" : STATUS_TEXT[p.status]}{p.review_note ? ` — “${p.review_note}”` : ""} · sent {fmtDT(p.created_at)}{p.reviewed_at ? ` · reviewed ${fmtDT(p.reviewed_at)}` : ""}</p>
            </div>
            <Pill tone={p.removed_at ? TONE_CLASSES.none : p.published_at ? TONE_CLASSES.ok : STATUS_TONE[p.status]} testid={`hs-library-proposal-status-${p.id}`}>{p.removed_at ? "removed" : p.published_at ? "published" : p.status}</Pill>
            {canEdit && p.status === "pending" && <Button size="sm" variant="ghost" className="h-7 text-xs text-muted-foreground" onClick={() => withdraw(p)} data-testid={`hs-library-proposal-withdraw-${p.id}`}><X size={12} /></Button>}
          </li>
        ))}
      </ul>
    </section>
  );
}
